/**
 * Warms the browser cache with the other variants' gallery images, so a colour
 * switch only waits for the section re-render instead of a fresh image download.
 *
 * The images come from a <template> rendered with the gallery's own srcset/sizes,
 * so the browser resolves the same file the gallery <img> will request later.
 * Loading starts once the main product image has loaded, or straight away when the
 * shopper points at or focuses the variant picker.
 */

/** @type {Set<string>} */
const warmed = new Set();

class BmpVariantMediaPreload extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    if (/** @type {any} */ (navigator).connection?.saveData) return;

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section') ?? document;

    /** @param {Event} event */
    const warmOnIntent = (event) => {
      if (event.target instanceof Element && event.target.closest('variant-picker')) this.#warm();
    };

    section.addEventListener('pointerover', warmOnIntent, { signal, passive: true });
    section.addEventListener('focusin', warmOnIntent, { signal });

    // Start as soon as the main product image is in, rather than waiting for the whole
    // page (window load), which on mobile often came after the first colour switch.
    const mainImage = document.querySelector('.product-information media-gallery .product-media__image');

    if (mainImage instanceof HTMLImageElement && !mainImage.complete) {
      mainImage.addEventListener('load', () => this.#warmSoon(), { once: true, signal });
      mainImage.addEventListener('error', () => this.#warmSoon(), { once: true, signal });
    } else {
      this.#warmSoon();
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #warmSoon() {
    setTimeout(() => this.#warm(), 100);
  }

  #warm() {
    const template = this.querySelector('template');
    if (!template) return;

    for (const source of template.content.querySelectorAll('img')) {
      const srcset = source.getAttribute('srcset') ?? '';
      const src = source.getAttribute('src') ?? '';
      const key = srcset || src;
      if (!key || warmed.has(key)) continue;

      warmed.add(key);
      const image = new Image();
      image.decoding = 'async';
      // sizes must be set before srcset so the browser picks the right candidate.
      image.sizes = source.getAttribute('sizes') ?? '';
      image.srcset = srcset;
      image.src = src;
    }
  }
}

if (!customElements.get('bmp-variant-media-preload')) {
  customElements.define('bmp-variant-media-preload', BmpVariantMediaPreload);
}
